import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader, Label2, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CloudCheck, CloudSlash, ArrowsClockwise, GitMerge } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Sync() {
  const [status, setStatus] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [online, setOnline] = useState(navigator.onLine);

  const load = useCallback(() => {
    api.get("/sync/status").then((r) => setStatus(r.data));
    api.get("/sync/conflicts").then((r) => setConflicts(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [load]);

  const resolve = async (cid, strategy) => {
    await api.post(`/sync/conflicts/${cid}/resolve`, { strategy });
    toast.success(strategy === "server" ? "Server-Version übernommen" : "Client-Version übernommen");
    load();
  };

  if (!status) return <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-md" />)}</div>;
  const openConf = conflicts.filter((c) => !c.resolved);

  return (
    <div>
      <PageHeader title="Synchronisation" subtitle="Offline-Sync · Präzedenz: Server gewinnt · Konfliktauflösung">
        <Button variant="outline" className="gap-2" data-testid="sync-refresh" onClick={load}><ArrowsClockwise size={18} />Aktualisieren</Button>
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="flex items-center gap-4 rounded-md border p-6" data-testid="sync-connection">
          {online ? <CloudCheck size={32} weight="duotone" className="text-success" /> : <CloudSlash size={32} weight="duotone" className="text-danger" />}
          <div><Label2>Verbindung</Label2><div className="font-heading text-xl font-bold">{online ? "Online" : "Offline"}</div></div>
        </Card>
        <Card className="flex items-center gap-4 rounded-md border p-6">
          <GitMerge size={32} weight="duotone" className="text-primary" />
          <div><Label2>Offene Konflikte</Label2><div className="font-heading text-xl font-bold">{status.open_conflicts}</div></div>
        </Card>
        <Card className="flex items-center gap-4 rounded-md border p-6">
          <ArrowsClockwise size={32} weight="duotone" className="text-primary" />
          <div><Label2>Präzedenz</Label2><div className="font-heading text-xl font-bold">Server gewinnt</div></div>
        </Card>
      </div>

      <h2 className="mb-3 mt-8 font-heading text-xl font-bold tracking-tight">Konflikte (manuelle Auflösung)</h2>
      <Card className="divide-y divide-border rounded-md border">
        {openConf.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Keine offenen Konflikte · kein stiller Datenverlust.</div>}
        {openConf.map((c) => (
          <div key={c.id} data-testid="conflict-row" className="flex flex-col justify-between gap-3 px-5 py-4 sm:flex-row sm:items-center">
            <div><div className="font-semibold">{c.entity} · {c.entity_id.slice(0, 8)}</div>
              <div className="text-xs text-muted-foreground">Server v{c.server_version} vs. Client-Basis v{c.base_version} · {new Date(c.ts).toLocaleString("de-DE")}</div></div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" data-testid={`resolve-server-${c.id}`} onClick={() => resolve(c.id, "server")}>Server übernehmen</Button>
              <Button size="sm" onClick={() => resolve(c.id, "client")}>Client übernehmen</Button>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}
